import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AddLicenseModal from './AddLicenseModal';
import {
  formatLicensePlanLabel,
  formatLicenseExpiry,
  maskLicenseCode,
  resolveLicenseTier,
} from '../utils/licenseDisplay';
import './LicenseSwitcher.css';

export interface LicenseData {
  id: string;
  code: string;
  type: string;
  username: string;
  expiryDate: string;
  isValid: boolean;
  isActive: boolean;
  hwid?: string;
  vpsIp?: string;
  vpsPort?: string;
  grabber?: boolean;
  mailer?: boolean;
  debouncer?: boolean;
  stripe?: boolean;
  premiumProxy?: boolean;
  totalScans?: number;
  level?: number;
}

interface LicenseSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchLicense: (license: LicenseData) => void;
  onAddLicense: () => void;
  currentLicense?: LicenseData;
  /** Panneau seul (sans overlay) — utilisé depuis le Dashboard */
  embedded?: boolean;
}

const LicenseSwitcher: React.FC<LicenseSwitcherProps> = ({
  isOpen,
  onClose,
  onSwitchLicense,
  onAddLicense,
  currentLicense,
  embedded = false,
}) => {
  const [licenses, setLicenses] = useState<LicenseData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadLicensesFromStorage();
    }
  }, [isOpen]);

  const loadLicensesFromStorage = () => {
    try {
      const savedLicenses = localStorage.getItem('multiple_licenses');
      if (savedLicenses) {
        const parsedLicenses: LicenseData[] = JSON.parse(savedLicenses);
        setLicenses(parsedLicenses);
      } else {
        const currentLicenseData = localStorage.getItem('user_license');
        const currentUserData = localStorage.getItem('license_user');

        if (currentLicenseData && currentUserData) {
          const savedLicense = JSON.parse(currentLicenseData);
          const savedUser = JSON.parse(currentUserData);

          const licenseData: LicenseData = {
            id: savedUser.id || savedLicense.hwid || 'current',
            code: savedLicense.code,
            type: savedLicense.type,
            username: savedLicense.username || savedUser.name,
            expiryDate: savedLicense.expiryDate,
            isValid: savedLicense.isValid,
            isActive: true,
            hwid: savedLicense.hwid,
            vpsIp: savedLicense.vpsIp,
            vpsPort: savedLicense.vpsPort,
            grabber: savedLicense.grabber || false,
            mailer: savedLicense.mailer || false,
            debouncer: savedLicense.debouncer || false,
            stripe: savedLicense.stripe || false,
            premiumProxy: savedLicense.premiumProxy || false,
            totalScans: savedLicense.totalScans,
            level: savedLicense.level,
          };

          setLicenses([licenseData]);
          saveLicensesToStorage([licenseData]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement licences:', error);
    }
  };

  const saveLicensesToStorage = (licensesData: LicenseData[]) => {
    try {
      localStorage.setItem('multiple_licenses', JSON.stringify(licensesData));
    } catch (error) {
      console.error('Erreur sauvegarde licences:', error);
    }
  };

  const handleSwitchLicense = async (license: LicenseData) => {
    setIsLoading(true);
    try {
      const updatedLicenses = licenses.map((l) => ({
        ...l,
        isActive: l.id === license.id,
      }));

      setLicenses(updatedLicenses);
      saveLicensesToStorage(updatedLicenses);

      const activeLicenseData = {
        code: license.code,
        type: license.type,
        username: license.username,
        expiryDate: license.expiryDate,
        isValid: license.isValid,
        hwid: license.hwid,
        vpsIp: license.vpsIp,
        vpsPort: license.vpsPort,
        grabber: license.grabber || false,
        mailer: license.mailer || false,
        debouncer: license.debouncer || false,
        stripe: license.stripe || false,
        premiumProxy: license.premiumProxy || false,
        totalScans: license.totalScans,
        level: license.level,
      };

      const activeUserData = {
        id: license.id,
        name: license.username,
        license: license.code,
      };

      localStorage.setItem('user_license', JSON.stringify(activeLicenseData));
      localStorage.setItem('license_user', JSON.stringify(activeUserData));

      window.dispatchEvent(
        new CustomEvent('licenseSwitched', {
          detail: { license: activeLicenseData, user: activeUserData },
        })
      );

      onSwitchLicense(license);
      onClose();
    } catch (error) {
      console.error('Erreur changement licence:', error);
    }
    setIsLoading(false);
  };

  const handleRemoveLicense = async (licenseId: string) => {
    if (licenses.length <= 1) {
      alert('You cannot remove your last license');
      return;
    }

    const confirmation = window.confirm('Are you sure you want to remove this license?');
    if (!confirmation) return;

    try {
      const updatedLicenses = licenses.filter((l) => l.id !== licenseId);
      setLicenses(updatedLicenses);
      saveLicensesToStorage(updatedLicenses);

      const removedLicense = licenses.find((l) => l.id === licenseId);
      if (removedLicense?.isActive && updatedLicenses.length > 0) {
        await handleSwitchLicense(updatedLicenses[0]);
      }
    } catch (error) {
      console.error('Erreur suppression licence:', error);
    }
  };

  const handleAddLicense = () => {
    setShowAddModal(true);
    onAddLicense();
  };

  const handleLicenseAdded = (licenseInfo: {
    code: string;
    type: string;
    username: string;
    expiryDate: string;
    isValid: boolean;
    hwid?: string;
    vpsIp?: string;
    vpsPort?: string;
    grabber?: boolean;
    mailer?: boolean;
    debouncer?: boolean;
    stripe?: boolean;
    premiumProxy?: boolean;
    totalScans?: number;
    level?: number;
  }) => {
    const isDuplicate = licenses.some(
      (license) =>
        license.code === licenseInfo.code ||
        license.username === licenseInfo.username ||
        license.hwid === licenseInfo.hwid
    );

    if (isDuplicate) {
      alert('This license is already on your account.');
      return;
    }

    const newLicenseData: LicenseData = {
      id: licenseInfo.hwid || `license_${Date.now()}`,
      code: licenseInfo.code,
      type: licenseInfo.type,
      username: licenseInfo.username,
      expiryDate: licenseInfo.expiryDate,
      isValid: licenseInfo.isValid,
      isActive: false,
      hwid: licenseInfo.hwid,
      vpsIp: licenseInfo.vpsIp,
      vpsPort: licenseInfo.vpsPort,
      grabber: licenseInfo.grabber || false,
      mailer: licenseInfo.mailer || false,
      debouncer: licenseInfo.debouncer || false,
      stripe: licenseInfo.stripe || false,
      premiumProxy: licenseInfo.premiumProxy || false,
      totalScans: licenseInfo.totalScans,
      level: licenseInfo.level,
    };

    const updatedLicenses = [...licenses, newLicenseData];
    const cleanLicenses = updatedLicenses.map((l) => ({
      ...l,
      isActive: l.id === newLicenseData.id,
    }));

    setLicenses(cleanLicenses);
    saveLicensesToStorage(cleanLicenses);
    setShowAddModal(false);
    alert(`License "${licenseInfo.username}" added successfully.`);
  };

  if (!isOpen) return null;

  const panel = (
    <div className="ls-panel">
      <header className="ls-header">
        <div className="ls-header-brand">
          <div className="ls-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 7h13l3-3M20 17H7l-3 3" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <div>
            <h2 className="ls-header-title">License manager</h2>
            <p className="ls-header-sub">
              {licenses.length} account{licenses.length !== 1 ? 's' : ''} saved
              {currentLicense?.type ? ` · active: ${formatLicensePlanLabel(currentLicense.type)}` : ''}
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="ls-close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      <div className="ls-body">
        {licenses.length === 0 ? (
          <div className="ls-empty">
            <div className="ls-empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="1.5">
                <circle cx="8" cy="12" r="4" />
                <path d="M12 12h9" strokeLinecap="round" />
                <path d="M17 9v6" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="ls-empty-title">No licenses yet</h3>
            <p className="ls-empty-desc">Add your first license to access the platform</p>
            <button type="button" onClick={handleAddLicense} className="ls-btn-add ls-btn-primary">
              Add a license
            </button>
          </div>
        ) : (
          <>
            <div className="ls-list">
              {licenses.map((license, index) => {
                const expiry = formatLicenseExpiry(license.expiryDate);
                const tier = resolveLicenseTier(license.type);
                const plan = formatLicensePlanLabel(license.type);

                return (
                  <motion.div
                    key={license.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`ls-card${license.isActive ? ' ls-card--active' : ''}`}
                  >
                    <div className="ls-card-inner">
                      <div className="ls-card-main">
                        <span className="ls-card-indicator" aria-hidden="true" />
                        <div className="ls-card-info">
                          <div className="ls-card-top">
                            <h3 className="ls-card-name">{license.username}</h3>
                            {license.isActive && <span className="ls-card-badge">Active</span>}
                            <span className={`ls-card-plan ls-card-plan--${tier}`}>{plan}</span>
                          </div>
                          <p className="ls-card-meta">{maskLicenseCode(license.code)}</p>
                          <p className={`ls-card-expiry ls-card-expiry--${expiry.status}`}>
                            {expiry.text}
                          </p>
                          {(license.grabber || license.mailer || license.debouncer || license.stripe || license.premiumProxy) && (
                            <div className="ls-card-addons">
                              {license.grabber && <span className="ls-card-addon">Grabber</span>}
                              {license.mailer && <span className="ls-card-addon">Mailer</span>}
                              {license.debouncer && <span className="ls-card-addon">Debouncer</span>}
                              {license.stripe && <span className="ls-card-addon">Stripe</span>}
                              {license.premiumProxy && <span className="ls-card-addon">Premium Proxy</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="ls-card-actions">
                        {!license.isActive && (
                          <button
                            type="button"
                            onClick={() => handleSwitchLicense(license)}
                            disabled={isLoading}
                            className="ls-btn-activate"
                          >
                            {isLoading ? '…' : 'Activate'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveLicense(license.id)}
                          className="ls-btn-remove"
                          title="Remove this license"
                          aria-label="Delete"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="ls-footer-add">
              <button type="button" onClick={handleAddLicense} className="ls-btn-add">
                <span aria-hidden="true">+</span>
                Add a license
              </button>
            </div>
          </>
        )}
      </div>

      <AddLicenseModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onLicenseAdded={handleLicenseAdded}
      />
    </div>
  );

  if (embedded) {
    return panel;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="cracker-modal-overlay"
      onClick={(e: React.MouseEvent) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {panel}
      </motion.div>
    </motion.div>
  );
};

export default LicenseSwitcher;
